import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import ProgressBar from '@repo/ui/progress-bar'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { constructClientPath } from '../../../../../../../packages/toolkit/src/utils/functions/construct-path'
import { canAccessEnrollmentHealth } from '../../../../api/enrollments/can-access-enrollment-health'
import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { getEnrollmentProfileCompletion } from '../../../../api/enrollments/get-enrollment-profile-completion'
import { getEnrollmentProfileCompletionPercentage } from '../../../../api/enrollments/get-enrollment-profile-completion-percentage'
import { enrollmentHasTest } from '../../../../api/enrollments/has-test'
import { Paths } from '../../../../lib/constants/path'
import { useObserver } from '../../../../lib/contexts/observer'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import { OtoIndividualTransferMethodEnum } from '../../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import {
  enrollmentBaseFields,
  EnrollmentBaseType
} from '../../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { DataProps } from '../../../../lib/types/utils'
import EnrollmentProgressIncicator from './enrollment-progress-incicator'

const ClientEnrollmentProgressCard = (props: DataProps<EnrollmentBaseType>) => {
  const { id: enrollmentId, data: enrollment } = useDataProps(props, (id) =>
    getEnrollment(id, enrollmentBaseFields)
  )

  const { navigate } = useNavigator()

  const enrollmentProfileCompletionPercentage = useQuery({
    queryKey: ['enrollment-profile-completion-percentage', enrollmentId],
    queryFn: () => getEnrollmentProfileCompletionPercentage(enrollmentId),
    enabled: !!enrollmentId
  })

  const enrollmentProfileCompletion = useQuery({
    queryKey: ['enrollment-profile-completion', enrollmentId],
    queryFn: () => getEnrollmentProfileCompletion(enrollmentId),
    enabled: !!enrollmentId
  })

  const { data: enrollmentHasTestData } = useQuery({
    queryKey: ['enrollment-has-test', enrollmentId],
    queryFn: () => {
      if (enrollmentId) return enrollmentHasTest(enrollmentId)
    },
    enabled: !!enrollmentId
  })

  const { data: canAccessEnrollmentHealthData, isPending: isHealthPending } = useQuery({
    queryKey: ['can-access-enrollment-health', enrollment?.id],
    queryFn: () => {
      if (enrollment) return canAccessEnrollmentHealth(enrollment.id)
    },
    enabled: !!enrollment
  })

  useObserver({
    key: 'update-staff-progress-bar',
    update: () => {
      enrollmentProfileCompletionPercentage.refetch()
      enrollmentProfileCompletion.refetch()
    }
  })

  if (
    enrollmentProfileCompletionPercentage.isPending ||
    enrollmentProfileCompletion.isPending ||
    isHealthPending
  )
    return <Loader center />

  const profileCompletion = enrollmentProfileCompletion.data
  const profileCompletionPercentage = enrollmentProfileCompletionPercentage.data

  const isOtoIndividual = EnrollmentProperties.isOtoIndividual(enrollment)
  const isOtoIndividualAirportPickup =
    enrollment?.otoIndividualProposition?.transferMethod ===
    OtoIndividualTransferMethodEnum.AIRPORT_PICKUP

  return (
    <InnerBox className="rounded-xl p-4 bg-white-hover flex flex-col gap-4">
      <InnerBox className="rounded-lg p-5 bg-white-active ">
        <ProgressBar
          text={'de vos informations sont complétées'}
          progress={profileCompletionPercentage || 0}
          colorStart={tailwindColors.primary.DEFAULT}
          colorEnd={tailwindColors.fushia.light.active}
        />
      </InnerBox>
      <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
        Le formulaire d'inscription de votre enfant est actuellement incomplet.
        <br /> Afin de nous permettre de préparer le séjour de votre enfant dans les meilleurs
        délais, veuillez remplir les informations manquantes sur les pages suivantes :
      </Typography>
      <InnerBox className="rounded-lg p-3 grid grid-cols-2 gap-3 mobile:grid-cols-1 mobile:gap-2">
        <EnrollmentProgressIncicator
          text="Photo d'identité de votre enfant"
          completed={profileCompletion?.photoSubmitted || false}
          onclick={() =>
            navigate(
              constructClientPath(
                Paths.CLIENT_PERSONAL_SPACE_INFORMATION_CHILD_PROFILE,
                enrollmentId
              )
            )
          }
        />
        {enrollmentHasTestData && (
          <EnrollmentProgressIncicator
            text="Test de niveau"
            completed={profileCompletion?.testSubmitted || false}
            onclick={() =>
              navigate(
                constructClientPath(
                  Paths.CLIENT_PERSONAL_SPACE_INFORMATION_TEST_LEVEL,
                  enrollmentId
                )
              )
            }
          />
        )}
        <EnrollmentProgressIncicator
          text="Informations complémentaires"
          completed={profileCompletion?.profileInfoSubmitted || false}
          onclick={() =>
            navigate(
              constructClientPath(
                Paths.CLIENT_PERSONAL_SPACE_INFORMATION_CHILD_PROFILE,
                enrollmentId
              )
            )
          }
        />

        {/* display when we don't have a transport in the main trip. A One to One Individuel
            enrollment has no journey, so it is driven by its transfer method instead: LEC only
            asks for the voyage individuel when it picks the youth up (accueil-transfert) */}
        {isOtoIndividual
          ? isOtoIndividualAirportPickup && (
              <EnrollmentProgressIncicator
                text="Remplissez votre planning de voyage individuel"
                completed={profileCompletion?.scheduleAvailable || false}
                onclick={() =>
                  navigate(
                    constructClientPath(
                      Paths.CLIENT_PERSONAL_SPACE_INFORMATION_INDIVIDUAL_TRIP,
                      enrollmentId
                    )
                  )
                }
              />
            )
          : !EnrollmentProperties.hasTransport(enrollment) && (
              <EnrollmentProgressIncicator
                text="Remplissez votre planning de voyage individuel"
                completed={profileCompletion?.scheduleAvailable || false}
                onclick={() => {}}
              />
            )}

        <EnrollmentProgressIncicator
          text="Papiers d'identité de mon enfant"
          completed={profileCompletion?.identityDocumentsSubmitted || false}
          onclick={() =>
            navigate(
              constructClientPath(Paths.CLIENT_PERSONAL_SPACE_INFORMATION_IDENTITY, enrollmentId)
            )
          }
        />
        {canAccessEnrollmentHealthData && (
          <EnrollmentProgressIncicator
            text="Fiche sanitaire de liaison"
            completed={profileCompletion?.healthFormSubmitted || false}
            onclick={() =>
              navigate(
                constructClientPath(
                  Paths.CLIENT_PERSONAL_SPACE_INFORMATION_MEDICAL_STATEMENT,
                  enrollmentId
                )
              )
            }
          />
        )}
      </InnerBox>
      <div className="flex items-center gap-1">
        <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
          Consultez impérativement et au plus vite l'onglet{' '}
          <Link
            to={constructClientPath(
              // a One to One Individuel parent is sent to the contacts of the stay instead
              EnrollmentProperties.isOtoIndividual(enrollment)
                ? Paths.CLIENT_PERSONAL_SPACE_TRIP_INFORMATION_OTO_CONTACT
                : Paths.CLIENT_PERSONAL_SPACE_DEPARTURE_CHECKLIST,
              enrollmentId
            )}
            className="text-primary underline"
          >
            Démarches administratives obligatoires
          </Link>
        </Typography>
      </div>
    </InnerBox>
  )
}

export default ClientEnrollmentProgressCard
